import classNames from "classnames";
import { NodeStates, VismaButtonNode } from "../../build/types";
import { useInteractionStates } from "../../hooks/useInteractionStates";
import { BuilderComponentProps } from "../BuilderInterface";
import {
    getColorsFromInteractiveStates,
    getCompoundStyle,
    getStateColorsForBuilder,
    getStateStyleForBuilder,
    getStylesFromInteractiveStates,
    limitCharacters
} from "../helpers";
import DynamicIcon from "../DynamicIcon";

const BuilderVismaButton = ({ node, builderProps, disabled, dynamicData, onClick }: BuilderComponentProps) => {
    const { id, attr, stateManager, text } = node;
    const { setElement, states } = useInteractionStates<HTMLButtonElement>();
    const vBNode = node as VismaButtonNode;
    const builderButtonClass = classNames("builder-button", {
        [`${attr?.className}`]: !!attr?.className
    });

    if (builderProps) {
        const { isContainer, nodeState, selectCallback, outlineCallback, onDragStart, onDragEnd } = builderProps;

        return (
            <button
                {...attr}
                id={id}
                className={builderButtonClass}
                style={getCompoundStyle({
                    node,
                    isContainer,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style")
                })}
                onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                onDragStart={e => onDragStart?.(e, id)}
                draggable={"true"}
                onDragEnd={onDragEnd}
            >
                {vBNode.icon?.src && (
                    <DynamicIcon
                        name={vBNode.icon?.src}
                        style={{
                            ...vBNode.icon?.style,
                            pointerEvents: "none",
                            ...getStateStyleForBuilder(stateManager, nodeState, "icon_style")
                        }}
                        colors={getStateColorsForBuilder(stateManager, nodeState, "icon_colors") || vBNode.icon.colors}
                    />
                )}
                {vBNode.prepend?.value && <span style={vBNode.prepend.style}>{vBNode.prepend.value}</span>}
                {vBNode.dynamicData ? limitCharacters(dynamicData || "", vBNode.characterLimit) : text}
                {vBNode.append?.value && <span style={vBNode.append.style}>{vBNode.append.value}</span>}
            </button>
        );
    } else {
        const { style, ...rest } = attr || {};

        const buttonStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: getCompoundStyle({ node }),
            stateManager,
            paths: ["attr_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const prependStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: vBNode.prepend?.style,
            stateManager,
            paths: ["prepend_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const appendStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: vBNode.append?.style,
            stateManager,
            paths: ["append_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const iconStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: vBNode.icon?.style,
            stateManager,
            paths: ["icon_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const iconColors = getColorsFromInteractiveStates({
            states,
            stateManager,
            defaultColors: vBNode.icon?.colors,
            paths: ["icon_colors"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        return (
            <button
                ref={setElement}
                id={id}
                {...rest}
                onClick={onClick}
                style={buttonStyle}
                className={builderButtonClass}
                disabled={disabled}
            >
                {vBNode.icon?.src && <DynamicIcon name={vBNode.icon?.src} style={iconStyle} colors={iconColors} />}
                {vBNode.prepend?.value && <span style={prependStyle}>{vBNode.prepend.value}</span>}
                {vBNode.dynamicData ? limitCharacters(dynamicData || "", vBNode.characterLimit) : text}
                {vBNode.append?.value && <span style={appendStyle}>{vBNode.append.value}</span>}
            </button>
        );
    }
};

export default BuilderVismaButton;
